import type { MediaRef } from "@dramapie/shared";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { formatDuration } from "../lib/format";
import { MEDIA_SLOT_LABEL, type MediaSlot } from "../lib/generations";

/** 第一次失败自动重试一次（契约 §2.9），用查询参数绕开浏览器缓存的失败结果。 */
function mediaSrc(url: string, attempt: number): string {
  if (attempt === 0) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}__retry=${attempt}`;
}

export interface PreviewTarget {
  shotId: string;
  shotOrder: number;
  slot: MediaSlot;
  media: MediaRef;
}

interface MediaTileProps {
  shotId: string;
  slot: MediaSlot;
  /** `null` = 这个槽位还没有媒体（首帧 / 尾帧占位）。 */
  media: MediaRef | null;
  shotOrder: number;
  /** 视频角标用的时长（秒）。 */
  duration?: number;
  onOpen: (target: PreviewTarget) => void;
}

/**
 * 竖屏 9:16 缩略图。
 *
 * 三种形态都有明确占位，绝不出现空白格：
 *   · 无媒体 / 未签名 → 「首帧待生成」占位
 *   · 有 URL → img / video（object-cover 裁成 9:16）
 *   · 加载失败 → 「加载失败」占位，点击重试
 */
export function MediaTile({
  shotId,
  slot,
  media,
  shotOrder,
  duration,
  onOpen,
}: MediaTileProps) {
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const label = MEDIA_SLOT_LABEL[slot];
  const url = media?.url ?? null;

  useEffect(() => {
    setFailed(false);
  }, [url, attempt]);

  function handleLoadError() {
    // §2.9：401/403 可能只是签名刚好过期，先自动重试一次，仍失败才出占位。
    if (attempt === 0) {
      setAttempt(1);
      return;
    }
    setFailed(true);
  }

  if (!media || !url) {
    return (
      <div
        role="img"
        aria-label={`第 ${shotOrder} 镜${label}占位`}
        className="flex aspect-[9/16] w-full flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border bg-surface text-center text-[10px] leading-tight text-muted"
      >
        <span aria-hidden="true" className="text-lg leading-none">
          ▦
        </span>
        <span>{label}待生成</span>
      </div>
    );
  }

  if (failed) {
    return (
      <button
        type="button"
        onClick={() => {
          setFailed(false);
          setAttempt((value) => value + 1);
        }}
        aria-label={`重新加载第 ${shotOrder} 镜${label}`}
        className="aspect-[9/16] w-full overflow-hidden rounded-lg border border-dashed border-danger/50 bg-surface"
      >
        <span
          role="img"
          aria-label={`第 ${shotOrder} 镜${label}加载失败`}
          className="flex h-full w-full flex-col items-center justify-center gap-1 text-center text-[10px] leading-tight text-danger"
        >
          <span aria-hidden="true" className="text-lg leading-none">
            ⚠
          </span>
          <span>{label}加载失败</span>
          <span className="text-muted">点击重试</span>
        </span>
      </button>
    );
  }

  const mediaElement =
    media.kind === "video" ? (
      <video
        key={attempt}
        src={mediaSrc(url, attempt)}
        muted
        playsInline
        preload="metadata"
        aria-label={`第 ${shotOrder} 镜${label}`}
        onError={handleLoadError}
        className="h-full w-full object-cover"
      />
    ) : (
      <img
        key={attempt}
        src={mediaSrc(url, attempt)}
        alt={`第 ${shotOrder} 镜${label}`}
        loading="lazy"
        onError={handleLoadError}
        className="h-full w-full object-cover"
      />
    );

  return (
    <button
      type="button"
      onClick={() => onOpen({ shotId, shotOrder, slot, media })}
      aria-label={`查看第 ${shotOrder} 镜${label}大图`}
      className="group relative block aspect-[9/16] w-full overflow-hidden rounded-lg border border-border bg-surface-2 hover:border-accent"
    >
      {mediaElement}
      {slot === "clip" && typeof duration === "number" ? (
        <span
          role="img"
          aria-label={`第 ${shotOrder} 镜${label}时长 ${formatDuration(duration)}`}
          className="absolute bottom-1 right-1 rounded px-1 py-0.5 text-[10px] font-medium tabular-nums"
          style={{ background: "var(--thumb-badge-bg)", color: "var(--thumb-badge-ink)" }}
        >
          {formatDuration(duration)}
        </span>
      ) : null}
    </button>
  );
}

/**
 * 大图预览：Esc 关闭、点遮罩关闭，关闭后焦点回到触发缩略图。
 * 挂到 body 上，避免被卡片的 `overflow-hidden` / 拖拽容器裁掉。
 */
export function MediaLightbox({
  target,
  onClose,
}: {
  target: PreviewTarget | null;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!target) return;
    const active = document.activeElement;
    restoreRef.current = active instanceof HTMLElement ? active : null;
    const timer = setTimeout(() => closeRef.current?.focus(), 0);

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", handleKeyDown);
      restoreRef.current?.focus();
    };
  }, [target, onClose]);

  if (!target) return null;

  const label = MEDIA_SLOT_LABEL[target.slot];

  return createPortal(
    <div
      className="dp-scrim"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`第 ${target.shotOrder} 镜${label}预览`}
        onClick={(event) => event.stopPropagation()}
        className="relative max-h-full max-w-full rounded-xl border border-border bg-surface p-2 shadow-xl"
      >
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="关闭预览"
          className="absolute right-1 top-1 z-10 rounded-lg px-2 py-0.5 text-lg leading-none"
          style={{ background: "var(--thumb-badge-bg)", color: "var(--thumb-badge-ink)" }}
        >
          ×
        </button>
        {target.media.kind === "video" ? (
          <video
            src={target.media.url ?? undefined}
            controls
            autoPlay
            playsInline
            aria-label={`第 ${target.shotOrder} 镜${label}预览`}
            className="max-h-[85vh] max-w-[85vw] rounded-lg"
            style={{ background: "var(--thumb-bg)" }}
          />
        ) : (
          <img
            src={target.media.url ?? undefined}
            alt={`第 ${target.shotOrder} 镜${label}大图`}
            className="max-h-[85vh] max-w-[85vw] rounded-lg object-contain"
          />
        )}
      </div>
    </div>,
    document.body,
  );
}
